/**
 * useProgressChart — one Tracker chart (weight or BMI) for the selected period.
 *
 * Wraps POST /UserProfile/GetChartData and keeps the period → date-range
 * mapping in shared code so both platforms request identical ranges.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import profileService from '../api/services/profileService';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import {
  getChartDateRange,
  mapPeriodicalType,
  type ChartName,
  type ChartPeriod,
} from '../utils/progress';
import type { ApiError } from '../types/api';
import type { ChartPoint } from '../types/userProfile';

export interface UseProgressChartResult {
  period: ChartPeriod;
  setPeriod: (period: ChartPeriod) => void;
  labels: string[];
  values: number[];
  minValue: number;
  maxValue: number;
  loading: boolean;
  error: string;
  refresh: () => void;
}

export const useProgressChart = (
  chartName: ChartName,
  initialPeriod: ChartPeriod = 'monthly',
): UseProgressChartResult => {
  const [period, setPeriod] = useState<ChartPeriod>(initialPeriod);
  const [labels, setLabels] = useState<string[]>([]);
  const [values, setValues] = useState<number[]>([]);
  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError('');

    try {
      const range = getChartDateRange(period);
      const response = await profileService.getChartData({
        from: range.from,
        to: range.to,
        periodicalType: mapPeriodicalType(period),
        chartName,
      });
      if (requestId !== requestIdRef.current) return;

      const data = response?.data;
      const points: ChartPoint[] = Array.isArray(data?.data?.chartData)
        ? (data?.data?.chartData as ChartPoint[])
        : [];

      if (data?.success === false) {
        setError(data?.message || '');
      }

      setLabels(points.map(point => point.xLabel ?? ''));
      setValues(
        points.map(point => {
          const value = Number(point.y);
          return Number.isFinite(value) ? value : 0;
        }),
      );
      setMinValue(Number(data?.data?.minValue) || 0);
      setMaxValue(Number(data?.data?.maxValue) || 0);
    } catch (caught) {
      if (requestId !== requestIdRef.current) return;
      setError(getApiErrorMessage(caught as ApiError));
      setLabels([]);
      setValues([]);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [chartName, period]);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load, reloadToken]);

  const refresh = useCallback(() => setReloadToken(token => token + 1), []);

  return {
    period,
    setPeriod,
    labels,
    values,
    minValue,
    maxValue,
    loading,
    error,
    refresh,
  };
};

export default useProgressChart;
